import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { apiRequest } from '../api/apiRequest';
import { TranslatableField, TranslationField, ProductTranslations } from '../types/translationTypes';

/**
 * djb2 hash – produces a short alphanumeric string for a given text.
 * Used as cache key for seller-level text cache.
 */
export function hashText(text: string): string {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash) + text.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Read the translations sub-object for a specific language from a product document.
 */
export async function getProductTranslation(
  productId: string,
  lang: string
): Promise<ProductTranslations | null> {
  try {
    const productRef = doc(db, 'products', productId);
    const snap = await getDoc(productRef);
    if (!snap.exists()) return null;
    const data = snap.data();
    return data?.translations?.[lang] ?? null;
  } catch {
    return null;
  }
}

/**
 * Save translated name/description for a product in Firestore.
 * Uses a targeted updateDoc so we don't overwrite unrelated product fields.
 */
export async function saveProductTranslation(
  productId: string,
  lang: string,
  data: ProductTranslations
): Promise<void> {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      [`translations.${lang}`]: data,
    });
  } catch (e) {
    console.warn('[translationCache] Could not save product translation:', e);
  }
}

/**
 * Read the hash-keyed text cache for a specific language from the seller document.
 */
export async function getSellerHashCache(
  uid: string,
  lang: string
): Promise<Record<string, string>> {
  try {
    const sellerRef = doc(db, 'sellers', uid);
    const snap = await getDoc(sellerRef);
    if (!snap.exists()) return {};
    const data = snap.data();
    return data?.translationCache?.[lang] ?? {};
  } catch {
    return {};
  }
}

/**
 * Merge new hash→translation entries into the seller translation cache for a given language.
 */
export async function saveSellerHashCache(
  uid: string,
  lang: string,
  entries: Record<string, string>
): Promise<void> {
  try {
    const sellerRef = doc(db, 'sellers', uid);
    // Build the update object with dot-notation keys so we merge rather than overwrite
    const updateObj: Record<string, string> = {};
    for (const [hash, text] of Object.entries(entries)) {
      updateObj[`translationCache.${lang}.${hash}`] = text;
    }
    await updateDoc(sellerRef, updateObj);
  } catch (e) {
    console.warn('[translationCache] Could not save seller hash cache:', e);
  }
}

/**
 * Collect all translatable fields from the form data.
 */
export function collectTranslatableFields(
  formData: {
    items: Array<{ id: string; name: string; description?: string | null; productId?: string | null }>;
    paymentTerms?: string | null;
    notes?: string | null;
    offerText?: string | null;
    projectReferenceName?: string | null;
  },
  isOffer = false
): TranslatableField[] {
  const fields: TranslatableField[] = [];

  // Items
  formData.items.forEach((item, index) => {
    if (item.name?.trim()) {
      fields.push({
        key: `item-${index}-name`,
        label: `Artikel ${index + 1}: Name`,
        originalText: item.name.trim(),
        productId: item.productId ?? undefined,
        fieldType: 'item_name',
        itemIndex: index,
      });
    }
    if (item.description?.trim()) {
      fields.push({
        key: `item-${index}-description`,
        label: `Artikel ${index + 1}: Beschreibung`,
        originalText: item.description.trim(),
        productId: item.productId ?? undefined,
        fieldType: 'item_description',
        itemIndex: index,
      });
    }
  });

  // Document-level fields
  if (formData.paymentTerms?.trim()) {
    fields.push({
      key: 'paymentTerms',
      label: 'Zahlungsbedingungen',
      originalText: formData.paymentTerms.trim(),
      fieldType: 'paymentTerms',
    });
  }
  if (formData.notes?.trim()) {
    fields.push({
      key: 'notes',
      label: 'Notizen',
      originalText: formData.notes.trim(),
      fieldType: 'notes',
    });
  }
  if (isOffer && formData.offerText?.trim()) {
    fields.push({
      key: 'offerText',
      label: 'Angebotstext',
      originalText: formData.offerText.trim(),
      fieldType: 'offerText',
    });
  }
  if (formData.projectReferenceName?.trim()) {
    fields.push({
      key: 'projectReferenceName',
      label: 'Projektname',
      originalText: formData.projectReferenceName.trim(),
      fieldType: 'projectName',
    });
  }

  return fields;
}

/**
 * Apply approved translations back to the form data.
 * Returns a new object with translated strings in place.
 */
export function applyTranslations<T extends {
  items: Array<{ name: string; description?: string | null }>;
  paymentTerms?: string | null;
  notes?: string | null;
  projectReferenceName?: string | null;
  offerText?: string | null;
}>(formData: T, approved: TranslationField[]): T {
  const translatedItems = formData.items.map((item, index) => {
    const nameTf = approved.find(f => f.key === `item-${index}-name`);
    const descTf = approved.find(f => f.key === `item-${index}-description`);
    return {
      ...item,
      name: nameTf ? nameTf.translatedText : item.name,
      description: descTf ? descTf.translatedText : item.description,
    };
  });

  const paymentTermsTf = approved.find(f => f.key === 'paymentTerms');
  const notesTf = approved.find(f => f.key === 'notes');
  const offerTextTf = approved.find(f => f.key === 'offerText');
  const projectNameTf = approved.find(f => f.key === 'projectReferenceName');

  return {
    ...formData,
    items: translatedItems,
    paymentTerms: paymentTermsTf ? paymentTermsTf.translatedText : formData.paymentTerms,
    notes: notesTf ? notesTf.translatedText : formData.notes,
    offerText: offerTextTf ? offerTextTf.translatedText : formData.offerText,
    projectReferenceName: projectNameTf ? projectNameTf.translatedText : formData.projectReferenceName,
  };
}

/**
 * Call the backend to translate uncached fields.
 * Returns a map of key → translatedText.
 */
export async function fetchTranslationsFromBackend(
  fields: TranslatableField[],
  targetLanguage: string,
  sourceLanguage = 'de'
): Promise<Record<string, string>> {
  if (fields.length === 0) return {};

  const response = await apiRequest('/translate-fields', {
    method: 'POST',
    body: JSON.stringify({
      fields: fields.map(f => ({ key: f.key, text: f.originalText })),
      targetLanguage,
      sourceLanguage,
    }),
  });

  if (!response.ok) {
    throw new Error(`Translation backend returned ${response.status}`);
  }

  const json = await response.json();
  return json.translations ?? {};
}
